import React, { useState, useRef, useEffect } from 'react';
import { Bucket } from '../types';
import { UploadBatch } from '../context/UploadContext';
import { useUploadContext } from '../context/UploadContext';
import UpgradePromptModal from './UpgradePromptModal/UpgradePromptModal';
import { SubscriptionTier } from '../types/subscription';
import { apiService } from '../services/api';

interface UploadObjectFormProps {
  selectedBucket: Bucket;
  onUploadComplete: () => void;
  storageQuota?: { usedGB: number; totalGB: number; percentUsed: number } | null;
}

const MAX_FILE_SIZE = 5 * 1024 * 1024 * 1024; // 5GB

const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

const getBatchProgress = (batch: UploadBatch): number => {
  let total = 0, uploaded = 0;
  batch.fileProgress.forEach(({ state }) => {
    total += state.totalBytes;
    uploaded += state.uploadedBytes;
  });
  if (total === 0) {
    return batch.totalFiles > 0 ? (batch.completedCount / batch.totalFiles) * 100 : 0;
  }
  return (uploaded / total) * 100;
};

const getDisplayName = (bucket: Bucket): string => {
  if (bucket.displayName) return bucket.displayName;
  // Format: crate-{uid8}-{region}-{name}
  const match = bucket.name.match(/^crate-[0-9a-f]{8}-[^-]+-(.+)$/);
  return match ? match[1] : bucket.name;
};

const UploadObjectForm: React.FC<UploadObjectFormProps> = ({ selectedBucket, onUploadComplete, storageQuota }) => {
  const { batches, startUpload, cancelBatch, dismissBatch } = useUploadContext();
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [selectionError, setSelectionError] = useState('');
  const [showStorageModal, setShowStorageModal] = useState(false);
  const [userTier, setUserTier] = useState<SubscriptionTier>('Free');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    apiService.getSubscription().then(res => {
      if (res.subscription?.tier) setUserTier(res.subscription.tier as SubscriptionTier);
    }).catch(() => {});
  }, []);

  const displayName = getDisplayName(selectedBucket);

  // Find the most relevant batch for this bucket: prefer actively uploading, else most recent
  const bucketBatches = batches.filter(b => b.bucketName === selectedBucket.name);
  const activeBatch = bucketBatches.find(b => b.isUploading) ?? bucketBatches[bucketBatches.length - 1] ?? null;

  useEffect(() => {
    if (activeBatch?.limitExceeded) {
      setShowStorageModal(true);
      dismissBatch(activeBatch.id);
    }
  }, [activeBatch?.limitExceeded]);

  // Suppress browser drag-and-drop overlay outside our drop zone
  useEffect(() => {
    const prevent = (e: DragEvent) => e.preventDefault();
    document.addEventListener('dragover', prevent);
    document.addEventListener('drop', prevent);
    return () => {
      document.removeEventListener('dragover', prevent);
      document.removeEventListener('drop', prevent);
    };
  }, []);

  const validateFiles = (files: File[]): string => {
    const oversized = files.filter(f => f.size > MAX_FILE_SIZE);
    if (oversized.length > 0) {
      return `${oversized.length} file(s) exceed the 5GB limit.`;
    }
    return '';
  };

  const wouldExceedQuota = (files: File[]): boolean => {
    if (!storageQuota) return false;
    const totalFileBytes = files.reduce((sum, f) => sum + f.size, 0);
    const remainingBytes = (storageQuota.totalGB - storageQuota.usedGB) * 1024 * 1024 * 1024;
    return totalFileBytes > remainingBytes;
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const err = validateFiles(files);
    if (err) { setSelectionError(err); return; }
    setSelectedFiles(files);
    setSelectionError('');
  };

  const collectFilesFromEntry = (entry: any): Promise<File[]> => {
    return new Promise((resolve) => {
      if (entry.isFile) {
        entry.file((file: File) => {
          Object.defineProperty(file, 'webkitRelativePath', {
            value: entry.fullPath.replace(/^\//, ''),
            writable: false,
          });
          resolve([file]);
        });
      } else if (entry.isDirectory) {
        const reader = entry.createReader();
        const allEntries: any[] = [];
        const readBatch = () => {
          reader.readEntries((entries: any[]) => {
            if (entries.length === 0) {
              Promise.all(allEntries.map((e: any) => collectFilesFromEntry(e)))
                .then(results => resolve(results.flat()));
            } else {
              allEntries.push(...entries);
              readBatch();
            }
          });
        };
        readBatch();
      } else {
        resolve([]);
      }
    });
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    if (activeBatch?.isUploading) return;

    const items = Array.from(e.dataTransfer.items);
    const fileArrays = await Promise.all(
      items
        .filter(item => item.kind === 'file')
        .map(item => {
          const entry = item.webkitGetAsEntry();
          return entry ? collectFilesFromEntry(entry) : Promise.resolve<File[]>([]);
        })
    );
    const files = fileArrays.flat();
    if (files.length === 0) return;

    const err = validateFiles(files);
    if (err) { setSelectionError(err); return; }

    if (wouldExceedQuota(files)) {
      setShowStorageModal(true);
      return;
    }

    setSelectionError('');
    setSelectedFiles([]);
    startUpload(files, selectedBucket, onUploadComplete);
  };

  const handleUpload = () => {
    if (selectedFiles.length === 0) return;
    const err = validateFiles(selectedFiles);
    if (err) { setSelectionError(err); return; }
    if (wouldExceedQuota(selectedFiles)) {
      setShowStorageModal(true);
      return;
    }
    setSelectionError('');
    startUpload(selectedFiles, selectedBucket, onUploadComplete);
    setSelectedFiles([]);
  };

  const getTotalSize = () => selectedFiles.reduce((t, f) => t + f.size, 0);

  const isUploading = activeBatch?.isUploading ?? false;

  return (
    <div className="object-list-container">
      <h2 style={{ fontSize: '1.5rem', fontWeight: 600, marginBottom: '1.5rem' }}>
        Add to {displayName}
      </h2>

      {/* Storage Quota Warning */}
      {storageQuota && storageQuota.percentUsed >= 80 && (
        <div style={{
          padding: '1rem',
          backgroundColor: storageQuota.percentUsed >= 95 ? '#fee' : '#fff3cd',
          borderLeft: `4px solid ${storageQuota.percentUsed >= 95 ? '#dc3545' : '#ffc107'}`,
          borderRadius: '4px',
          marginBottom: '1rem',
        }}>
          <div style={{ fontWeight: 600, color: storageQuota.percentUsed >= 95 ? '#721c24' : '#856404', marginBottom: '0.25rem' }}>
            {storageQuota.percentUsed >= 95 ? '⚠️ Storage Almost Full!' : '⚠️ Storage Warning'}
          </div>
          <div style={{ fontSize: '0.875rem', color: storageQuota.percentUsed >= 95 ? '#721c24' : '#856404' }}>
            You've used {storageQuota.usedGB} GB of your {storageQuota.totalGB} GB limit ({storageQuota.percentUsed}%).
            {storageQuota.percentUsed >= 95 && ' Please upgrade your plan to continue uploading.'}
          </div>
        </div>
      )}

      {/* File / Folder selection */}
      <div className="form-group">
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            onClick={() => fileInputRef.current?.click()}
            className="btn"
            disabled={isUploading}
            style={{ flex: 1 }}
          >
            Select Files
          </button>
          <button
            onClick={() => folderInputRef.current?.click()}
            className="btn"
            disabled={isUploading}
            style={{ flex: 1 }}
          >
            Select Folder
          </button>
        </div>
        <input ref={fileInputRef} type="file" onChange={handleFileSelect} style={{ display: 'none' }} disabled={isUploading} multiple />
        <input ref={folderInputRef} type="file" onChange={handleFileSelect} style={{ display: 'none' }} disabled={isUploading} {...({ webkitdirectory: '', directory: '' } as any)} multiple />

        {/* Drag and Drop Zone */}
        <div
          onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); if (!isUploading) setIsDragOver(true); }}
          onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(false); }}
          onDrop={handleDrop}
          style={{
            marginTop: '0.75rem',
            border: `2px dashed ${isDragOver ? '#667eea' : '#d1d5db'}`,
            borderRadius: '8px',
            padding: '1.75rem 1rem',
            textAlign: 'center',
            backgroundColor: isDragOver ? '#f0f4ff' : '#fafafa',
            cursor: isUploading ? 'not-allowed' : 'default',
            transition: 'border-color 0.15s ease, background-color 0.15s ease',
            userSelect: 'none',
          }}
        >
          <div style={{ fontSize: '1.5rem', marginBottom: '0.4rem', color: isDragOver ? '#667eea' : '#9ca3af' }}>&#8595;</div>
          <div style={{ fontSize: '0.875rem', fontWeight: 500, color: isDragOver ? '#667eea' : '#6b7280' }}>
            {isDragOver ? 'Drop to upload' : 'Drag & drop files or folders here'}
          </div>
          <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.2rem' }}>
            Upload starts automatically
          </div>
        </div>
      </div>

      {/* Selection error */}
      {selectionError && (
        <div className="form-error">{selectionError}</div>
      )}

      {/* Pending selection (before upload) */}
      {selectedFiles.length > 0 && !isUploading && (
        <div style={{ marginBottom: '1rem', padding: '1rem', background: '#f9fafb', borderRadius: '8px' }}>
          <div style={{ fontWeight: 500 }}>{selectedFiles.length} file(s) selected</div>
          <div style={{ fontSize: '0.875rem', color: '#666', marginTop: '0.25rem' }}>
            Total size: {formatBytes(getTotalSize())}
          </div>
          {selectedFiles.length <= 5 && (
            <div style={{ marginTop: '0.5rem', fontSize: '0.875rem' }}>
              {selectedFiles.map((file, idx) => (
                <div key={idx} style={{ color: '#666' }}>• {file.webkitRelativePath || file.name}</div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Inline progress for this bucket's active batch */}
      {activeBatch?.isUploading && (
        <div style={{ marginBottom: '1rem', padding: '1rem', background: '#f9fafb', borderRadius: '8px' }}>
          <div style={{ fontWeight: 500, marginBottom: '0.5rem' }}>
            Uploading {activeBatch.completedCount} / {activeBatch.totalFiles} files
          </div>
          <div className="progress-bar" style={{ marginBottom: '0.5rem' }}>
            <div className="progress-fill" style={{ width: `${getBatchProgress(activeBatch)}%` }} />
          </div>
          <div style={{ fontSize: '0.875rem', color: '#666', marginBottom: '0.75rem' }}>
            {getBatchProgress(activeBatch).toFixed(1)}%
          </div>
          {/* Active file list (up to 10) */}
          {Array.from(activeBatch.fileProgress.values())
            .filter(p => p.state.status === 'uploading')
            .slice(0, 10)
            .map(({ file, state }) => (
              <div
                key={`${file.name}-${file.size}`}
                style={{ marginBottom: '0.5rem', padding: '0.5rem', background: 'white', borderRadius: '4px', border: '1px solid #e5e7eb' }}
              >
                <div style={{ fontSize: '0.75rem', fontWeight: 500, marginBottom: '0.25rem' }}>
                  {(file as any).webkitRelativePath || file.name}
                </div>
                <div className="progress-bar" style={{ height: '4px', marginBottom: '0.2rem' }}>
                  <div className="progress-fill" style={{ width: `${state.progress}%` }} />
                </div>
                <div style={{ fontSize: '0.7rem', color: '#999' }}>
                  {state.progress.toFixed(0)}% · {formatBytes(state.uploadedBytes)} / {formatBytes(state.totalBytes)}
                </div>
              </div>
            ))}
          <button
            onClick={() => cancelBatch(activeBatch.id)}
            className="btn"
            style={{ marginTop: '0.5rem', background: '#ef4444', color: 'white', border: 'none', width: '100%' }}
          >
            Cancel Upload
          </button>
        </div>
      )}

      {/* Success for this bucket's batch */}
      {activeBatch && !activeBatch.isUploading && !activeBatch.error && (
        <div className="form-success">
          {activeBatch.completedCount} file(s) uploaded successfully!
        </div>
      )}

      {/* Error for this bucket's batch */}
      {activeBatch?.error && (
        <div className="form-error">{activeBatch.error}</div>
      )}

      {/* Upload button */}
      {!isUploading && selectedFiles.length > 0 && (
        <div style={{ marginTop: '1rem' }}>
          <button
            onClick={handleUpload}
            className="btn btn-primary"
            style={{ width: '100%' }}
          >
            Upload {selectedFiles.length} File(s)
          </button>
        </div>
      )}

      <UpgradePromptModal
        isOpen={showStorageModal}
        onClose={() => setShowStorageModal(false)}
        limitType="storage"
        currentUsage={storageQuota ? storageQuota.usedGB * 1024 * 1024 * 1024 : 0}
        limit={storageQuota ? storageQuota.totalGB * 1024 * 1024 * 1024 : 0}
        currentTier={userTier}
      />
    </div>
  );
};

export default UploadObjectForm;
